/**
 * Orquestra o dialog de envio WhatsApp já existente nesta branch
 * (SendWhatsAppMessageDialog / FluxZap). Não recria templates internos.
 * @module useEcommerceSendMessage
 */

import { useCallback, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import type { Patient } from "fhir/r5";
import {
  SendWhatsAppMessageDialog,
  useMessagingTemplatesForSend,
  type MessagingPatient,
} from "src-next/modules/crm-clinical/messaging";
import { mapPatientToMessagingPatient } from "../helpers/mapPatientToMessagingPatient";

interface UseEcommerceSendMessageParams {
  organizationId?: string;
  practitionerId?: string;
  hasActiveMetaEndpoint?: boolean;
}

/**
 * Abre o dialog de envio com um ou vários pacientes da tela ecommerce.
 */
export function useEcommerceSendMessage({
  organizationId,
  practitionerId,
  hasActiveMetaEndpoint,
}: UseEcommerceSendMessageParams) {
  const [open, setOpen] = useState(false);
  const [messagingPatients, setMessagingPatients] = useState<
    MessagingPatient[]
  >([]);
  const onSentRef = useRef<(() => void) | undefined>();

  const { templates } = useMessagingTemplatesForSend({ organizationId });

  const appointment = useMemo(
    () => ({
      id: "ecommerce-ad-hoc",
      startAtIso: dayjs().toISOString(),
    }),
    [],
  );

  const openSend = useCallback(
    (patients: Patient[], options?: { onSent?: () => void }) => {
      if (patients.length === 0) return;
      onSentRef.current = options?.onSent;
      setMessagingPatients(patients.map(mapPatientToMessagingPatient));
      setOpen(true);
    },
    [],
  );

  const closeSend = useCallback(() => {
    setOpen(false);
    setMessagingPatients([]);
    onSentRef.current = undefined;
  }, []);

  const handleMessageSent = useCallback(() => {
    onSentRef.current?.();
    closeSend();
  }, [closeSend]);

  const isSingle = messagingPatients.length === 1;

  const dialog = (
    <SendWhatsAppMessageDialog
      open={open}
      onClose={closeSend}
      appointment={appointment}
      organizationId={organizationId}
      practitionerId={practitionerId}
      templates={templates}
      patient={isSingle ? messagingPatients[0] : null}
      initialSelectedPatients={isSingle ? undefined : messagingPatients}
      staticPatientListForSelection={isSingle ? undefined : messagingPatients}
      taskPatientReference={
        isSingle && messagingPatients[0]?.id
          ? `Patient/${messagingPatients[0].id}`
          : null
      }
      hasActiveMetaEndpoint={hasActiveMetaEndpoint}
      onMessageSent={handleMessageSent}
    />
  );

  return { openSend, closeSend, dialog };
}
